import * as React from "react";
import { RouterLink as Link } from "../router";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../breadcrumb";
import { Check, ChevronRight, Link2, MoreHorizontal } from "../icons";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { cn } from "../utils";

/** One hop in a breadcrumb trail: a display label + the href it navigates to.
 *  `href` is optional — a crumb without one renders as plain text (the only
 *  hrefless crumb in practice is a current/leaf label a caller couldn't link). */
export interface TrailCrumb {
  label: string;
  href?: string;
}

export interface CrumbTrailProps {
  /**
   * The ordered crumbs AFTER any fixed root rendered by the caller (e.g. the
   * top-nav project switcher). The last crumb is the current page and renders
   * as non-clickable text; every earlier crumb is a navigable link.
   */
  crumbs: TrailCrumb[];
  /**
   * Collapse the middle of the trail behind a `…` once the trail has MORE than
   * this many crumbs (default 3). When collapsed the first crumb + the last two
   * stay visible and the rest fold into a hover/click popover whose entries are
   * each clickable. Set `0`/`Infinity` to disable collapsing.
   */
  collapseAfter?: number;
  /** Separator glyph between crumbs. Slash matches the Figma top-nav; chevron
   *  matches the in-page PageHeader trail. Default chevron. */
  separator?: "slash" | "chevron";
  /** Render a separator BEFORE the first crumb too — used when the trail joins
   *  to a root crumb the caller rendered to its left (the project switcher). */
  leadingSeparator?: boolean;
  /** Classes on the outer `<nav>`. */
  className?: string;
  /** Classes on the `<ol>` list (gap, color, nowrap…). */
  listClassName?: string;
  /** Classes applied to every crumb label (e.g. truncation). */
  crumbClassName?: string;
  /** Extra classes for the current (last) crumb on top of `crumbClassName`. */
  currentClassName?: string;
  /**
   * Turn the current (last) crumb into a copy-the-page-link affordance: it
   * renders muted like the rest of the trail, brightens on hover/focus, reveals
   * a link glyph, and copies the current page URL to the clipboard on click
   * (with a transient "Link copied" confirmation). Used by the global top-nav
   * crumb; off by default so the in-page PageHeader trail keeps a plain leaf.
   */
  copyCurrentLink?: boolean;
}

/**
 * The canonical clickable breadcrumb trail. Every crumb but the current page is
 * a real link to that level, and when the trail runs deeper than `collapseAfter`
 * the middle folds into a `…` that reveals the hidden levels on hover and on
 * click — each one still a navigable link. Built on the `Breadcrumb` atoms so it
 * inherits the design-system markup; the collapse popover is the one piece the
 * atoms don't provide.
 *
 * Used by the global top-nav crumb (`NavBreadcrumb`, slash separators, a leading
 * slash joining it to the project switcher) and by `PageHeader` (chevron, no
 * leading separator). Pure presentation — callers derive the `crumbs`.
 */
export function CrumbTrail({
  crumbs,
  collapseAfter = 3,
  separator = "chevron",
  leadingSeparator = false,
  className,
  listClassName,
  crumbClassName,
  currentClassName,
  copyCurrentLink = false,
}: CrumbTrailProps) {
  if (crumbs.length === 0) return null;

  const lastIndex = crumbs.length - 1;

  // Collapse the middle when the trail is deeper than the threshold: keep the
  // first crumb and the last two, fold everything between into the ellipsis.
  // Requires ≥4 crumbs so there is always ≥1 hidden level worth folding.
  const shouldCollapse =
    collapseAfter > 0 && crumbs.length > collapseAfter && crumbs.length >= 4;
  const hidden = shouldCollapse ? crumbs.slice(1, crumbs.length - 2) : [];

  const items: React.ReactNode[] = [];
  const pushSep = (key: string) => {
    items.push(<Sep key={key} variant={separator} />);
  };
  const pushCrumb = (crumb: TrailCrumb, index: number) => {
    const isCurrent = index === lastIndex;
    items.push(
      <BreadcrumbItem key={`crumb-${index}`}>
        {isCurrent && copyCurrentLink ? (
          <CurrentCrumbCopy
            label={crumb.label}
            labelClassName={cn(crumbClassName, currentClassName)}
          />
        ) : isCurrent ? (
          <BreadcrumbPage className={cn(crumbClassName, currentClassName)}>
            {crumb.label}
          </BreadcrumbPage>
        ) : crumb.href ? (
          <BreadcrumbLink asChild className={crumbClassName}>
            <Link href={crumb.href}>{crumb.label}</Link>
          </BreadcrumbLink>
        ) : (
          // No href to navigate to → plain text (still part of the trail).
          <span className={crumbClassName}>{crumb.label}</span>
        )}
      </BreadcrumbItem>,
    );
  };

  if (leadingSeparator) pushSep("lead");

  if (shouldCollapse) {
    // first
    pushCrumb(crumbs[0]!, 0);
    // … (hidden middle)
    pushSep("sep-ellipsis");
    items.push(
      <BreadcrumbItem key="ellipsis">
        <CrumbEllipsis crumbs={hidden} />
      </BreadcrumbItem>,
    );
    // last two
    for (let i = crumbs.length - 2; i < crumbs.length; i++) {
      pushSep(`sep-${i}`);
      pushCrumb(crumbs[i]!, i);
    }
  } else {
    crumbs.forEach((crumb, i) => {
      if (i > 0) pushSep(`sep-${i}`);
      pushCrumb(crumb, i);
    });
  }

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList className={listClassName}>{items}</BreadcrumbList>
    </Breadcrumb>
  );
}

/** Legacy clipboard fallback for contexts where `navigator.clipboard` is
 *  unavailable (older engines / non-secure origins). Returns whether it stuck. */
function legacyCopy(text: string): boolean {
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/**
 * The current-page crumb rendered as a copy-the-link affordance. It reads muted
 * like the rest of the trail (so the leaf no longer sits at full strength), then
 * brightens and reveals a link glyph on hover/focus to signal it's clickable.
 * Clicking copies the current page URL to the clipboard and flashes a transient
 * "Link copied" chip anchored just below the crumb — the toast/confirmation the
 * app has no global surface for. Keyboard- and screen-reader-friendly
 * (`role="status"` announces the copy).
 */
function CurrentCrumbCopy({
  label,
  labelClassName,
}: {
  label: string;
  labelClassName?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const flash = React.useCallback(() => {
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  }, []);

  const copy = React.useCallback(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (!url) return;
    // Prefer the async Clipboard API (secure contexts: https + localhost); fall
    // back to the legacy execCommand path so the confirmation still fires where
    // it's unavailable. Only flash "copied" once a write actually succeeds.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(flash, () => legacyCopy(url) && flash());
    } else if (legacyCopy(url)) {
      flash();
    }
  }, [flash]);

  return (
    <span className="relative inline-flex items-center">
      {/* button-ignore: a breadcrumb copy-link affordance, not a styled action button */}
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy link to ${label}`}
        aria-current="page"
        className="group/crumb inline-flex items-center gap-1 rounded text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:text-foreground"
      >
        <span className={labelClassName}>{label}</span>
        <span className="inline-flex size-3.5 shrink-0 items-center justify-center opacity-0 transition-opacity group-hover/crumb:opacity-100 group-focus-visible/crumb:opacity-100">
          {copied ? (
            <Check className="size-3 text-success" />
          ) : (
            <Link2 className="size-3" />
          )}
        </span>
      </button>
      {copied ? (
        // badge-ignore: transient "Link copied" toast (role="status", pointer-events-none, absolutely positioned) — an ephemeral confirmation, not a categorical status tag
        <span
          role="status"
          className="pointer-events-none absolute top-full left-1/2 z-50 mt-1.5 -translate-x-1/2 rounded-md border border-border bg-popover px-2 py-1 type-subtext whitespace-nowrap text-popover-foreground shadow-md"
        >
          Link copied
        </span>
      ) : null}
    </span>
  );
}

/** Separator: a muted slash (Figma top-nav) or a chevron (in-page trail). */
function Sep({ variant }: { variant: "slash" | "chevron" }) {
  if (variant === "slash") {
    return (
      <BreadcrumbSeparator className="text-muted-foreground/60">
        /
      </BreadcrumbSeparator>
    );
  }
  return (
    <BreadcrumbSeparator className="text-muted-foreground/60">
      <ChevronRight />
    </BreadcrumbSeparator>
  );
}

/**
 * The collapsed `…` affordance: a popover that opens on HOVER (so you can peek
 * the folded path) and on CLICK / keyboard (so it works on touch + for keyboard
 * users), listing every hidden level as a clickable link. Non-modal + no
 * focus-steal so a hover never traps the page.
 */
function CrumbEllipsis({ crumbs }: { crumbs: TrailCrumb[] }) {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const scheduleClose = React.useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  }, [cancelClose]);
  React.useEffect(() => cancelClose, [cancelClose]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        {/* button-ignore: a breadcrumb-collapse affordance, not a styled action button */}
        <button
          type="button"
          aria-label={`Show ${crumbs.length} hidden ${crumbs.length === 1 ? "level" : "levels"}`}
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          className="flex items-center justify-center rounded px-0.5 text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:text-foreground"
        >
          <MoreHorizontal className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        // Keep focus on the trigger when the popover opens on hover so the page
        // never yanks focus from under the user; clicking a link still works.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        className="w-auto min-w-[9rem] max-w-[20rem] p-1"
      >
        <ul className="flex flex-col">
          {crumbs.map((crumb, i) => (
            <li key={`${crumb.href ?? crumb.label}-${i}`}>
              {crumb.href ? (
                <Link
                  href={crumb.href}
                  onClick={() => setOpen(false)}
                  className="block truncate rounded-md px-2 py-1.5 type-body text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="block truncate rounded-md px-2 py-1.5 type-body text-muted-foreground">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
